import { AlertCircle, CalendarDate, CheckCircle, Clock } from '@untitled-ui/icons-react/build/cjs';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { DaysPendingState, getDaysPendingDisplay } from '../documentHub.helper';

/**
 * Icon per urgency state — the icon carries the same meaning as the colour so the badge stays
 * readable without relying on colour alone (colour-blind users, greyscale printing).
 * Keyed by DaysPendingState, so adding a state to the helper surfaces here as a type error.
 */
const STATE_ICON: Record<Exclude<DaysPendingState, 'settled'>, React.ElementType> = {
    paid: CheckCircle,
    overdue: AlertCircle,
    dueSoon: Clock,
    onTime: CalendarDate,
};

/**
 * Icon colour per urgency state. Client decision — only the icon carries colour, the label text
 * next to it stays the table's normal neutral colour (no red/amber/green text).
 */
const STATE_ICON_COLOR: Record<Exclude<DaysPendingState, 'settled'>, string> = {
    paid: 'text-fgSuccessPrimary',
    overdue: 'text-danger500',
    dueSoon: 'text-warning700',
    // Client decision — comfortably-ahead-of-due-date reads as informational, so the icon is blue
    // (matches BadgeCmp's own info colour) rather than the grey it used to share with the settled
    // em-dash.
    onTime: 'text-blue500',
};

interface IDaysPendingBadgeProps {
    /** `dueDate - today` in whole days, straight from the API. Null/undefined => settled/paid. */
    daysPending: number | null | undefined;
    /** Invoice status (e.g. 'paid', 'pending', 'overdue') — only 'paid' changes this cell's display. */
    status?: string;
}

/**
 * DaysPendingBadge — the Days Pending cell: an icon + label, no date sub-line, whose icon (only —
 * client decision, the label text stays neutral) tracks how urgent payment is (overdue / due soon
 * / on time).
 *
 * Shared by InvoiceRow (the real table) and InvoiceDraggableCell (the column-settings drag preview)
 * so the two can never drift — they each used to carry their own copy of this logic.
 */
const DaysPendingBadge = ({ daysPending, status }: IDaysPendingBadgeProps) => {
    const { t } = useTranslation();
    const { state, label, badgeType } = getDaysPendingDisplay(daysPending, t, status);

    // Settled invoices get a plain dash — nothing to flag, no icon needed.
    if (state === 'settled' || !badgeType) {
        return <span className="text-grayLight600 leading-[18px]">{label}</span>;
    }

    const Icon = STATE_ICON[state];

    return (
        <span className="flex items-center text-grayLight600 leading-[18px] whitespace-nowrap">
            <Icon className={`w-3 h-3 flex-none mr-1 ${STATE_ICON_COLOR[state]}`} />
            {label}
        </span>
    );
};

export default DaysPendingBadge;
